import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { isValidIpv4 } from '../../../libs.jsx';
import SearchableSelect from '../../../components/v2/SearchableSelect.jsx';

/**
 * ServerDrawer — slide-out drawer for the Add Server form.
 *
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   onSave: (data: object) => Promise<void>,
 *   areas: Array<{ area_id: number, area_name: string }>,
 *   campusId: number,
 * }} props
 */
const ServerDrawer = ({ isOpen, onClose, onSave, areas, campusId }) => {
  const drawerRef = useRef(null);

  // Form state
  const [macAddr, setMacAddr] = useState('');
  const [serverType, setServerType] = useState('');
  const [isBackup, setIsBackup] = useState(false);
  const [interfaceId, setInterfaceId] = useState('');
  const [description, setDescription] = useState('');
  const [areaId, setAreaId] = useState('');
  const [ipAddress, setIpAddress] = useState('');
  const [saving, setSaving] = useState(false);

  // Reset form on open
  useEffect(() => {
    if (isOpen) {
      setMacAddr('');
      setServerType('');
      setIsBackup(false);
      setInterfaceId('');
      setDescription('');
      setAreaId('');
      setIpAddress('');
      setSaving(false);
    }
  }, [isOpen]);

  // Escape key handler
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Focus first input on open
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        const firstInput = drawerRef.current?.querySelector('input, select');
        firstInput?.focus();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Validation
  const macPattern = /^([0-9A-Fa-f]{2}[:\-]){5}([0-9A-Fa-f]{2})$|^[0-9A-Fa-f]{12}$/;
  const descPattern = /^[0-9a-zA-Z_ \-]+$/;
  const isMacValid = macPattern.test(macAddr.trim());
  const isDescValid = description.trim() && descPattern.test(description.trim());
  // IP is optional — blank is fine, but anything entered must be a valid IPv4
  const isIpValid = !ipAddress.trim() || isValidIpv4(ipAddress);
  const isFormValid = isMacValid && serverType && interfaceId && isDescValid && areaId && isIpValid;

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (!isFormValid) return;
      setSaving(true);

      // Clean MAC address — remove : and -
      const cleanedMac = macAddr.trim().replace(/[:\-]/g, '');

      const formData = {
        mac_addr: cleanedMac,
        campus_id: campusId,
        size: serverType,
        description: description.trim(),
        product_id: 5,
        interface_id: parseInt(interfaceId, 10),
        area_id: parseInt(areaId, 10),
        is_active: !isBackup,
        palcare_interface_id: parseInt(interfaceId, 10),
        ip_address: ipAddress.trim(),
      };

      onSave(formData).finally(() => setSaving(false));
    },
    [isFormValid, macAddr, serverType, isBackup, interfaceId, description, areaId, ipAddress, campusId, onSave]
  );

  // Sort areas alphabetically (numeric-aware so "Room 2" precedes "Room 10").
  // No occupancy gating here — a server is equipment assigned to an area, not
  // a resident occupying a bed.
  const areaOptions = useMemo(
    () => [...(areas || [])]
      .sort((a, b) => (a.area_name || '').localeCompare(b.area_name || '', undefined, {
        sensitivity: 'base',
        numeric: true,
      }))
      .map((a) => ({
        value: a.area_id.toString(),
        label: a.area_name,
        searchText: a.area_name || '',
      })),
    [areas]
  );

  return (
    <>
      {/* Backdrop */}
      {isOpen ? (
        <div className="dp-drawer-backdrop" onClick={onClose} aria-hidden="true" />
      ) : null}

      {/* Drawer panel */}
      <div
        ref={drawerRef}
        className={`dp-drawer ${isOpen ? 'is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Add Server"
      >
        {/* Header */}
        <div className="dp-drawer__header">
          <h2 className="dp-drawer__title">Add Server</h2>
          <button
            type="button"
            onClick={onClose}
            className="dp-drawer__close"
            aria-label="Close drawer"
          >
            <svg className="dp-icon-lg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="dp-drawer__body">
          {/* MAC Address */}
          <div className="dp-drawer__field">
            <label htmlFor="sv-mac" className="dp-drawer__label">
              MAC Address <span className="dp-drawer__required">*</span>
            </label>
            <input
              id="sv-mac"
              type="text"
              value={macAddr}
              onChange={(e) => setMacAddr(e.target.value)}
              placeholder="AA:BB:CC:DD:EE:FF or AABBCCDDEEFF"
              className={`dp-drawer__input ${macAddr && !isMacValid ? 'is-invalid' : ''}`}
              required
            />
            {macAddr && !isMacValid ? (
              <p className="dp-drawer__error">Enter a valid MAC address (e.g. AA:BB:CC:DD:EE:FF)</p>
            ) : null}
          </div>

          {/* Server Type */}
          <div className="dp-drawer__field">
            <label htmlFor="sv-type" className="dp-drawer__label">
              Server Type <span className="dp-drawer__required">*</span>
            </label>
            <select
              id="sv-type"
              value={serverType}
              onChange={(e) => setServerType(e.target.value)}
              className="dp-drawer__select"
              required
            >
              <option value="">Select server type...</option>
              <option value="1">Green Server</option>
              <option value="2">White Server</option>
            </select>
          </div>

          {/* Backup checkbox */}
          <div className="dp-drawer__field dp-drawer__field--row">
            <input
              id="sv-backup"
              type="checkbox"
              checked={isBackup}
              onChange={(e) => setIsBackup(e.target.checked)}
              className="dp-checkbox"
            />
            <label htmlFor="sv-backup" className="dp-drawer__label dp-drawer__label--inline">
              Backup Server
            </label>
          </div>

          {/* Interface */}
          <div className="dp-drawer__field">
            <label htmlFor="sv-interface" className="dp-drawer__label">
              Interface <span className="dp-drawer__required">*</span>
            </label>
            <select
              id="sv-interface"
              value={interfaceId}
              onChange={(e) => setInterfaceId(e.target.value)}
              className="dp-drawer__select"
              required
            >
              <option value="">Select interface...</option>
              <option value="1">Hard-Wired</option>
              <option value="2">Firepanel</option>
              <option value="3">Inovonics</option>
            </select>
          </div>

          {/* Description */}
          <div className="dp-drawer__field">
            <label htmlFor="sv-description" className="dp-drawer__label">
              Description <span className="dp-drawer__required">*</span>
            </label>
            <input
              id="sv-description"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Server description"
              className={`dp-drawer__input ${description && !isDescValid ? 'is-invalid' : ''}`}
              required
            />
            {description && !isDescValid ? (
              <p className="dp-drawer__error">Only letters, numbers, spaces, dashes, and underscores</p>
            ) : null}
          </div>

          {/* Local IP Address (optional) */}
          <div className="dp-drawer__field">
            <label htmlFor="sv-ip" className="dp-drawer__label">
              Local IP Address
            </label>
            <input
              id="sv-ip"
              type="text"
              value={ipAddress}
              onChange={(e) => setIpAddress(e.target.value)}
              placeholder="192.168.1.10 (optional)"
              inputMode="numeric"
              className={`dp-drawer__input ${ipAddress && !isIpValid ? 'is-invalid' : ''}`}
            />
            {ipAddress && !isIpValid ? (
              <p className="dp-drawer__error">Enter a valid IP address (e.g. 192.168.1.10)</p>
            ) : null}
          </div>

          {/* Area */}
          <div className="dp-drawer__field">
            <label htmlFor="sv-area" className="dp-drawer__label">
              Area <span className="dp-drawer__required">*</span>
            </label>
            <SearchableSelect
              id="sv-area"
              options={areaOptions}
              value={areaId}
              onChange={(val) => setAreaId(val || '')}
              placeholder="Search or select area..."
              ariaLabel="Area"
              required
              isClearable
            />
          </div>

          {/* Footer */}
          <div className="dp-drawer__footer">
            <button
              type="button"
              onClick={onClose}
              className="dp-btn dp-btn--secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isFormValid || saving}
              className="dp-btn dp-btn--primary"
            >
              {saving ? 'Adding...' : 'Add Server'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default ServerDrawer;
